import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { AiBidConversation } from "lib/hooks/api/bid/scorecard/useGetBidChat";
import { EventNames, useTracking } from "lib/tracking";

type QuestionsProps = {
  chats?: AiBidConversation[];
  isLoading: boolean;
  selectedConversationId: string | undefined;
  setSelectedConversationId: (id: string | undefined) => void;
  bidId: string;
};

export default function AskStotlesAiChats({
  chats,
  isLoading,
  selectedConversationId,
  setSelectedConversationId,
  bidId,
}: QuestionsProps) {
  const { logEvent } = useTracking();

  return (
    <Flex gap={32} flexDirection="column">
      <Button
        icon="edit02"
        onClick={() => {
          logEvent(EventNames.newChatCreated, {
            "Criteria name": "Ad-hoc question",
            "Resource ID": bidId,
          });
          setSelectedConversationId(undefined);
        }}
      >
        New Chat
      </Button>
      <ChatHistory>
        {isLoading && (
          <>
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
            <SkeletonText width="100%" />
          </>
        )}
        {chats?.map((c) => (
          <ConversationPane
            key={c.conversationId}
            conversation={c}
            selectedConversationId={selectedConversationId}
            setSelectedConversationId={setSelectedConversationId}
          />
        ))}
      </ChatHistory>
    </Flex>
  );
}

const ChatHistory = styled(Flex)({
  flexDirection: "column",
  gap: 32,
  overflowY: "auto",
  maxHeight: "80%",
});

type ConversationPaneProps = {
  conversation: AiBidConversation;
  selectedConversationId: string | undefined;
  setSelectedConversationId: (id: string) => void;
};

function ConversationPane({
  conversation,
  selectedConversationId,
  setSelectedConversationId,
}: ConversationPaneProps): JSX.Element | null {
  const { logEvent } = useTracking();

  const firstQuestion = conversation.questionAndAnswer[0]?.question;

  if (!firstQuestion) {
    return null;
  }

  return (
    <Button
      key={conversation.conversationId}
      buttonType="secondary"
      justifyContent="flex-start"
      onClick={() => {
        logEvent(EventNames.chatSelected, {
          "Chat type": "Ad-hoc question",
          "Conversation ID": conversation.conversationId,
          "Resource ID": conversation.resourceId,
        });
        setSelectedConversationId(conversation.conversationId);
      }}
    >
      <ClassNames>
        {({ css, theme }) => (
          <EllipsisTooltipText
            fullText={firstQuestion}
            containerClassname={css({
              display: "-webkit-box",
              WebkitLineClamp: 1,
              lineClamp: 1,
              WebkitBoxOrient: "vertical",
              maxWidth: 250,
              color: `${
                selectedConversationId === conversation.conversationId
                  ? theme.colors.sysPrimaryDefault
                  : "inherit"
              } !important`,
            })}
          />
        )}
      </ClassNames>
    </Button>
  );
}
